import { createRoot } from 'react-dom/client';
import Auth from 'opensooq-sso';
import { ToastContainer } from 'react-toastify';
import { getCookies, getOriginDomain, hasNoPassword } from 'app/helpers/commonFunctions';
import { OpenSooqI18nProvider } from 'app/i18n/OpenSooqi18n';
import AppRoutes from 'app/routing/AppRoutes';
import { generateToken } from 'newApp/helpers/auth';
import { defaultOptions, deleteCookie, setCookie } from 'newApp/helpers/cookies';
import { getOpenSooqSiteUrl } from 'newApp/helpers/openSooq';
import 'newApp/assets/css/index.css';
import 'react-loading-skeleton/dist/skeleton.css';

// Kept as a static import: react-toastify is already anchored in the entry chunk by
// app/helper/SystemAlert, so importing it lazily here saved 0.6 kB and only added a
// Suspense boundary on the startup path.
const container = document.getElementById('root');
const root = container ? createRoot(container) : null;
const SSO_RETURN_URL_COOKIE = 'returnUrl';

// MainLayout wraps every route, so its chunk is always needed, but it would only be
// requested once SSO resolves and the first render runs. Starting it here overlaps
// that download with the SSO round trip below.
void import('newApp/layouts/main-layout/MainLayout');

const initSSO = async () => {
  // One snapshot instead of a call per field: each getCookies() re-parses
  // document.cookie 18 times and runs 4 JSON.parse calls.
  const cookies = getCookies();

  if (!cookies.ecountry) {
    if (process.env.REACT_APP_ENV !== 'localhost') {
      window.open(process.env.REACT_APP_PUBLIC_URL?.replace('my', 'www') + '/', '_self');
    }
    throw new Error('country not found');
  } else {
    const newAuth = Auth.getInstance();
    const isLoggedIn = await newAuth.init(
      getOriginDomain(),
      'sso',
      cookies.NEXT_LOCALE,
      cookies.ecountry,
      cookies.source,
      cookies.device_uuid,
      cookies.cityId,
    );

    if (isLoggedIn.state === 'LoggedOut' || hasNoPassword()) {
      if (process.env.REACT_APP_ENV !== 'localhost') {
        await Auth.getInstance().authenticate(window.location.href, true);
      }
    }

    const identity = await newAuth.getIdentity();

    if (isLoggedIn.state === 'LoggedIn' && identity?.isNewUser) {
      const openSooqUrl = getOpenSooqSiteUrl(cookies.ecountry, cookies.NEXT_LOCALE || 'ar');

      setCookie(SSO_RETURN_URL_COOKIE, encodeURIComponent(openSooqUrl), defaultOptions);

      try {
        // Keep MY unmounted while Complete advances through SSO. SSO reads the
        // returnUrl cookie and sends No/close back to the main OpenSooq website.
        await generateToken();
      } finally {
        deleteCookie(SSO_RETURN_URL_COOKIE);
      }
    }

    // Read again rather than reusing the snapshot: the auth step above can change
    // the language cookie.
    const { NEXT_LOCALE } = getCookies();
    const selectedLang = NEXT_LOCALE === 'ku' ? 'ar' : NEXT_LOCALE || 'ar';

    localStorage.setItem('i18nConfig', JSON.stringify({ selectedLang }));
  }
};

initSSO()
  .then(() => {
    root?.render(
      <OpenSooqI18nProvider>
        <ToastContainer
          className="!tw-w-full min-[901px]:!tw-w-[1200px]"
          autoClose={false}
          draggableDirection="y"
          draggablePercent={60}
        />
        <AppRoutes />
      </OpenSooqI18nProvider>,
    );
  })
  .catch(() => {
    if (process.env.REACT_APP_ENV !== 'localhost') {
      window.location.href = process.env.REACT_APP_PUBLIC_URL?.replace('my', 'www') + '/';
    }
    root?.render(<></>);
  });
